import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { pageTransition, formatDate } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useToast } from "@/hooks/useToast";
import {
  clockIn, clockOut, getActiveEntry, createManualEntry,
  listTimeEntries, listTimesheets, submitTimesheet, reviewTimesheet,
} from "@/api/time";
import { PageHeader } from "@/components/layout/PageHeader";
import { ExportCsvButton } from "@/components/shared/ExportCsvButton";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Tabs } from "@/components/ui/Tabs";
import { DataTable, type Column } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { WeeklyTimesheetGrid } from "./WeeklyTimesheetGrid";
import { PendingReviewsPanel } from "./PendingReviewsPanel";
import {
  Play, Square, Clock, Plus, Check, X, Timer,
} from "lucide-react";
import type { TimeEntry, Timesheet } from "@/types/time";
import { TIME_ENTRY_STATUS_LABELS, TIMESHEET_STATUS_LABELS } from "@/types/time";
import type { PaginatedResponse } from "@/types/common";

export function TimeTrackingPage() {
  const { hasRole } = usePermissions();
  const { addToast } = useToast();
  const online = useOnlineStatus();

  const [activeTab, setActiveTab] = useState("entries");
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);
  const [entries, setEntries] = useState<PaginatedResponse<TimeEntry> | null>(null);
  const [timesheets, setTimesheets] = useState<PaginatedResponse<Timesheet> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Clock in form
  const [projectCode, setProjectCode] = useState("");
  const [clockNotes, setClockNotes] = useState("");

  // Manual entry modal
  const [showManual, setShowManual] = useState(false);
  const [manualIn, setManualIn] = useState("");
  const [manualOut, setManualOut] = useState("");
  const [manualProject, setManualProject] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Elapsed timer
  const [elapsed, setElapsed] = useState("");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [active, entryData, tsData] = await Promise.all([
        getActiveEntry().catch(() => null),
        listTimeEntries(page),
        listTimesheets(1, 20),
      ]);
      setActiveEntry(active);
      setEntries(entryData);
      setTimesheets(tsData);
    } catch {
      addToast("error", "Failed to load time data");
    } finally {
      setIsLoading(false);
    }
  }, [page, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Timer update
  useEffect(() => {
    if (!activeEntry) {
      setElapsed("");
      return;
    }
    // The backend stores `clock_in` as a timezone-aware UTC timestamp, but
    // some serializations return it without an explicit tz suffix (e.g.
    // "2026-04-24T21:48:00"). JavaScript's `new Date()` parses a suffix-less
    // ISO string as LOCAL time, which on a UTC-offset browser produces a
    // "future" moment and a negative elapsed value (e.g. "-5:-1:-15").
    // Normalize: if there's no timezone indicator, treat it as UTC.
    const parseAsUtc = (iso: string): number => {
      if (!iso) return Date.now();
      const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
      return new Date(hasTz ? iso : iso + "Z").getTime();
    };
    const startMs = parseAsUtc(activeEntry.clock_in);
    const update = () => {
      const diff = Math.max(0, Date.now() - startMs);
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setElapsed(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [activeEntry]);

  const handleClockIn = async () => {
    try {
      const result = await clockIn(projectCode || undefined, clockNotes || undefined);
      if (result && (result as { queued_offline?: boolean }).queued_offline) {
        addToast("info", "You're offline — clock-in queued and will sync when you're back.");
      } else {
        addToast("success", "Clocked in");
      }
      setProjectCode("");
      setClockNotes("");
      fetchData();
    } catch {
      addToast("error", "Failed to clock in");
    }
  };

  const handleClockOut = async () => {
    if (!activeEntry) return;
    try {
      const result = await clockOut(activeEntry.id);
      if (result && (result as { queued_offline?: boolean }).queued_offline) {
        addToast("info", "You're offline — clock-out queued and will sync when you're back.");
      } else {
        // Optimistically clear the active entry so the timer stops
        // immediately — don't wait for fetchData(), which can lag behind
        // a stale SW cache response.
        setActiveEntry(null);
        setElapsed("");
        addToast("success", `Clocked out — ${result.hours}h logged`);
      }
      fetchData();
    } catch (err: unknown) {
      // Surface the real reason: 404 = no active entry on the server
      // (often because the entry was already closed in another tab or
      // the cached active-entry response is stale). Re-fetch so the
      // UI reflects backend truth, then show a useful toast instead of
      // the generic "Failed to clock out".
      const ax = err as {
        response?: { status?: number; data?: { detail?: unknown } };
        message?: string;
      };
      const status = ax?.response?.status;
      const detail = ax?.response?.data?.detail;
      console.warn("clockOut failed", { status, detail, err });
      if (status === 404) {
        addToast(
          "info",
          "This time entry is no longer active — refreshing.",
        );
        // Clear the stale entry and re-sync the page so the user sees
        // the real state.
        setActiveEntry(null);
        setElapsed("");
        fetchData();
        return;
      }
      const msg =
        typeof detail === "string"
          ? detail
          : status === 401
            ? "Your session expired — please refresh and try again."
            : "Failed to clock out — please try again.";
      addToast("error", msg);
    }
  };

  const handleManualEntry = async () => {
    if (!manualIn || !manualOut) return;
    setIsSubmitting(true);
    try {
      await createManualEntry(manualIn, manualOut, manualProject || undefined, manualNotes || undefined);
      addToast("success", "Time entry added");
      setShowManual(false);
      setManualIn("");
      setManualOut("");
      setManualProject("");
      setManualNotes("");
      fetchData();
    } catch {
      addToast("error", "Failed to add time entry");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitTimesheet = async (tsId: string) => {
    try {
      await submitTimesheet(tsId);
      addToast("success", "Timesheet submitted");
      fetchData();
    } catch {
      addToast("error", "Failed to submit timesheet");
    }
  };

  const handleReviewTimesheet = async (tsId: string, status: "APPROVED" | "REJECTED") => {
    try {
      await reviewTimesheet(tsId, status);
      addToast("success", `Timesheet ${status.toLowerCase()}`);
      fetchData();
    } catch {
      addToast("error", "Failed to review timesheet");
    }
  };

  const entryColumns: Column<TimeEntry>[] = [
    {
      key: "date",
      header: "Date",
      render: (e) => (
        <span className="text-text-primary text-sm">
          {formatDate(e.clock_in, "MMM d, yyyy")}
        </span>
      ),
    },
    {
      key: "time",
      header: "Time",
      render: (e) => (
        <span className="text-text-muted text-xs">
          {formatDate(e.clock_in, "HH:mm")}
          {e.clock_out ? ` — ${formatDate(e.clock_out, "HH:mm")}` : " — …"}
        </span>
      ),
    },
    {
      key: "hours",
      header: "Hours",
      render: (e) => (
        <span className="font-medium text-text-primary">
          {e.hours ? `${e.hours}h` : "—"}
        </span>
      ),
    },
    {
      key: "project",
      header: "Project",
      render: (e) => (
        <span className="text-text-muted text-sm">{e.project_code || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (e) => (
        <Badge
          variant={
            e.status === "ACTIVE" ? "success" :
            e.status === "COMPLETED" ? "default" : "info"
          }
        >
          {TIME_ENTRY_STATUS_LABELS[e.status]}
        </Badge>
      ),
    },
  ];

  const canReview = hasRole("MANAGER", "HR_ADMIN", "SUPER_ADMIN");
  const canExport = hasRole("HR_ADMIN", "SUPER_ADMIN");

  const tabs = [
    { id: "entries", label: "Time Entries", count: entries?.total },
    { id: "weekly", label: "Weekly grid" },
    { id: "timesheets", label: "Timesheets", count: timesheets?.total },
    ...(canReview ? [{ id: "reviews", label: "Pending reviews" }] : []),
  ];

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Time Tracking"
        subtitle="Log hours and manage timesheets"
        actions={
          <div className="flex items-center gap-2">
            <ExportCsvButton resource="time-entries" />
            <Button variant="secondary" onClick={() => setShowManual(true)}>
              <Plus className="h-4 w-4" />
              Manual Entry
            </Button>
          </div>
        }
      />

      {!online && (
        <div
          role="status"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200"
        >
          You're offline. Clock-in / clock-out actions will be queued and sent
          automatically when your connection comes back.
        </div>
      )}

      {/* Clock In/Out Card */}
      <Card className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-accent/10 p-3">
              <Timer className="h-6 w-6 text-accent" />
            </div>
            <div>
              {activeEntry ? (
                <>
                  <p className="text-sm text-text-muted">Currently clocked in</p>
                  <p className="text-3xl font-bold font-heading text-text-primary tabular-nums">
                    {elapsed}
                  </p>
                  {activeEntry.project_code && (
                    <p className="text-xs text-text-muted mt-1">
                      Project: {activeEntry.project_code}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-lg font-medium text-text-primary">Ready to work?</p>
                  <p className="text-sm text-text-muted">Clock in to start tracking time</p>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {!activeEntry && (
              <>
                <Input
                  placeholder="Project code"
                  value={projectCode}
                  onChange={(e) => setProjectCode(e.target.value)}
                  className="w-32"
                />
                <Input
                  placeholder="Notes"
                  value={clockNotes}
                  onChange={(e) => setClockNotes(e.target.value)}
                  className="w-40"
                />
              </>
            )}
            {activeEntry ? (
              <Button variant="danger" onClick={handleClockOut}>
                <Square className="h-4 w-4" />
                Clock Out
              </Button>
            ) : (
              <Button onClick={handleClockIn}>
                <Play className="h-4 w-4" />
                Clock In
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6" />

      {activeTab === "entries" && (
        <div className="bg-surface rounded-xl border border-border">
          <DataTable
            columns={entryColumns}
            data={entries?.items ?? []}
            keyExtractor={(e) => e.id}
            isLoading={isLoading}
            page={page}
            totalPages={entries?.total_pages ?? 1}
            onPageChange={setPage}
            emptyState={
              <EmptyState
                icon={Clock}
                title="No time entries yet"
                description={
                  activeEntry
                    ? "You're clocked in right now — once you clock out, the entry will land here."
                    : "Clock in to start tracking time. Entries roll up into weekly timesheets you can submit for approval."
                }
                action={
                  activeEntry
                    ? undefined
                    : {
                        label: "Clock In",
                        onClick: handleClockIn,
                      }
                }
              />
            }
          />
        </div>
      )}

      {activeTab === "weekly" && (
        <WeeklyTimesheetGrid canExport={canExport} />
      )}

      {activeTab === "reviews" && canReview && <PendingReviewsPanel />}

      {activeTab === "timesheets" && (
        <div className="space-y-3">
          {(timesheets?.items ?? []).length === 0 ? (
            <EmptyState
              icon={Clock}
              title="No timesheets"
              description="Timesheets are generated automatically from your time entries."
            />
          ) : (
            (timesheets?.items ?? []).map((ts) => (
              <Card key={ts.id} className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge
                      variant={
                        ts.status === "APPROVED" ? "success" :
                        ts.status === "REJECTED" ? "danger" :
                        ts.status === "SUBMITTED" ? "info" : "default"
                      }
                    >
                      {TIMESHEET_STATUS_LABELS[ts.status]}
                    </Badge>
                    {ts.overtime_hours > 0 && (
                      <Badge variant="warning">
                        {ts.overtime_hours}h OT
                      </Badge>
                    )}
                  </div>
                  <p className="font-medium text-text-primary">
                    {formatDate(ts.week_start, "MMM d")} — {formatDate(ts.week_end, "MMM d, yyyy")}
                  </p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-text-muted">
                    <span>{ts.total_hours}h total</span>
                    <span>{ts.regular_hours}h regular</span>
                    {ts.employee_name && <span>{ts.employee_name}</span>}
                    <span>{ts.entries.length} entries</span>
                  </div>
                </div>

                <div className="flex gap-2 shrink-0">
                  {ts.status === "DRAFT" && (
                    <Button size="sm" onClick={() => handleSubmitTimesheet(ts.id)}>
                      Submit
                    </Button>
                  )}
                  {ts.status === "SUBMITTED" && hasRole("MANAGER", "HR_ADMIN", "SUPER_ADMIN") && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleReviewTimesheet(ts.id, "APPROVED")}
                      >
                        <Check className="h-4 w-4" />
                        Approve
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReviewTimesheet(ts.id, "REJECTED")}
                      >
                        <X className="h-4 w-4" />
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Manual Entry Modal */}
      <Modal
        isOpen={showManual}
        onClose={() => setShowManual(false)}
        title="Add Manual Time Entry"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Clock In"
            type="datetime-local"
            value={manualIn}
            onChange={(e) => setManualIn(e.target.value)}
          />
          <Input
            label="Clock Out"
            type="datetime-local"
            value={manualOut}
            onChange={(e) => setManualOut(e.target.value)}
          />
          <Input
            label="Project Code (optional)"
            value={manualProject}
            onChange={(e) => setManualProject(e.target.value)}
            placeholder="e.g., PROJ-001"
          />
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Notes (optional)
            </label>
            <textarea
              value={manualNotes}
              onChange={(e) => setManualNotes(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              rows={2}
              placeholder="What did you work on?"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowManual(false)}>
              Cancel
            </Button>
            <Button onClick={handleManualEntry} isLoading={isSubmitting}>
              Add Entry
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
